import { z } from 'zod';
import { aisleOf, type Aisle } from './aisles';

/**
 * Recipes (1.21, Küche 2): households/{hid}/recipes/{id}. A family's own collection – typed in,
 * from a link (schema.org, no AI) or from a screenshot/photo (AI, Plus). Every way ends in the
 * same shape, so the recipe page, the cooking view and "auf die Liste" work the same for all.
 */

/** Tags the family sets (Mike, 15.10.: fixed tags to start, own ones later). */
export const RECIPE_TAGS = ['veggie', 'kids'] as const;
export const RecipeTag = z.enum(RECIPE_TAGS);
export type RecipeTag = z.infer<typeof RecipeTag>;

/** Up to this many minutes counts as "Schnell" – computed, nobody has to tag it. */
export const QUICK_MINUTES = 30;
/** Not cooked for this many days counts as "Lange nicht". */
export const LONG_AGO_DAYS = 30;

export const Ingredient = z.object({
  /** Amount for the recipe's servings; null for "Salz und Pfeffer", "etwas Öl". */
  qty: z.number().positive().max(100_000).nullable().default(null),
  /** Upper end of a range ("2–3 Zehen"). */
  qtyMax: z.number().positive().max(100_000).nullable().default(null),
  unit: z.string().trim().max(20).default(''),
  name: z.string().trim().min(1).max(80),
  /** "gewürfelt", "zum Bestreuen" – shown, never put on the shopping list. */
  note: z.string().trim().max(80).default(''),
});
export type Ingredient = z.infer<typeof Ingredient>;

export const RecipeStep = z.object({
  text: z.string().trim().min(1).max(1000),
  /** Timer the step offers ("10 Minuten köcheln" → 10). */
  minutes: z
    .number()
    .int()
    .positive()
    .max(24 * 60)
    .nullable()
    .default(null),
});
export type RecipeStep = z.infer<typeof RecipeStep>;

export const RecipeSourceKind = z.enum(['own', 'link', 'photo', 'idea']);
export const RecipeSource = z.object({
  kind: RecipeSourceKind,
  /** The page a link recipe came from – kept so the family can go back to the original. */
  url: z.string().url().max(500).nullable().default(null),
  /** Short name of the site ("chefkoch.de") or the book ("Unser Lieblingsbuch, S. 82"). */
  note: z.string().trim().max(120).default(''),
});
export type RecipeSource = z.infer<typeof RecipeSource>;

/** What the family edits – the same for typing in, after an import and when changing it. */
export const RecipeDraft = z.object({
  title: z.string().trim().min(1).max(120),
  servings: z.number().int().min(1).max(50).default(4),
  /** Total time in minutes (null = unknown). */
  minutes: z
    .number()
    .int()
    .positive()
    .max(48 * 60)
    .nullable()
    .default(null),
  ingredients: z.array(Ingredient).max(80).default([]),
  steps: z.array(RecipeStep).max(60).default([]),
  tags: z.array(RecipeTag).max(RECIPE_TAGS.length).default([]),
  /** "Chili erst am Tisch" – one line for the kids' plate. */
  kidsTip: z.string().trim().max(200).default(''),
  source: RecipeSource.default({ kind: 'own', url: null, note: '' }),
});
export type RecipeDraft = z.infer<typeof RecipeDraft>;

export const Recipe = RecipeDraft.extend({
  createdBy: z.string().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
  /** Set when the family finishes the cooking view – drives "Lange nicht". */
  lastCooked: z.number().nullable().default(null),
  cookCount: z.number().int().nonnegative().default(0),
  /**
   * The family's own photo (1.21) as a small preview for tiles (data URL, ~10 KB); the full
   * photo lives in recipes/{id}/photo/main so lists stay light. Photos from sites are never
   * copied (Mike, 15.10.).
   */
  thumb: z.string().max(40_000).nullable().default(null),
});
export type Recipe = z.infer<typeof Recipe>;

/** Own photo: base64 JPEG, already scaled down on the device. */
export const RecipePhotoInput = z.object({
  id: z.string().min(1).max(64),
  /** null removes the photo */
  photo: z.string().min(16).max(400_000).nullable(),
  thumb: z.string().min(16).max(40_000).nullable(),
});

// ---------------------------------------------------------------------------------------------
// Ingredients: "400 g Spaghetti", "1/2 TL Salz", "2–3 Zehen Knoblauch, gehackt"
// ---------------------------------------------------------------------------------------------

const UNITS = [
  'kg',
  'g',
  'mg',
  'l',
  'ml',
  'cl',
  'dl',
  'el',
  'tl',
  'msp',
  'prise',
  'prisen',
  'dose',
  'dosen',
  'packung',
  'packungen',
  'pck',
  'pkg',
  'päckchen',
  'becher',
  'bund',
  'zehe',
  'zehen',
  'stück',
  'stk',
  'scheibe',
  'scheiben',
  'tasse',
  'tassen',
  'glas',
  'gläser',
  'handvoll',
  'stange',
  'stangen',
  'zweig',
  'zweige',
  'blatt',
  'blätter',
  'würfel',
  'kugel',
  'kugeln',
  'cup',
  'cups',
  'tbsp',
  'tsp',
  'tablespoon',
  'tablespoons',
  'teaspoon',
  'teaspoons',
  'oz',
  'lb',
  'lbs',
  'pinch',
  'can',
  'cans',
  'clove',
  'cloves',
  'slice',
  'slices',
  'bunch',
  'handful',
  'sprig',
  'sprigs',
  'piece',
  'pieces',
  'pack',
  'packet',
];
const UNIT_SET = new Set(UNITS);

const FRACTIONS: Record<string, number> = {
  '½': 0.5,
  '¼': 0.25,
  '¾': 0.75,
  '⅓': 1 / 3,
  '⅔': 2 / 3,
  '⅛': 0.125,
};

/** "1,5" "1.5" "1/2" "1 1/2" "½" "1½" → number; null when it is no amount. */
export function parseQty(text: string): number | null {
  const t = text.trim().replace(',', '.');
  const mixed = /^(\d+)?\s*([½¼¾⅓⅔⅛])$/.exec(t);
  if (mixed) return Number(mixed[1] ?? 0) + FRACTIONS[mixed[2]!]!;
  const frac = /^(?:(\d+)\s+)?(\d+)\/(\d+)$/.exec(t);
  if (frac) return Number(frac[1] ?? 0) + Number(frac[2]) / Number(frac[3]);
  if (/^\d+(?:\.\d+)?$/.test(t)) return Number(t);
  return null;
}

const QTY = String.raw`(\d+\s+\d+\/\d+|\d+\/\d+|\d*\s?[½¼¾⅓⅔⅛]|\d+(?:[.,]\d+)?)`;
const LEAD = new RegExp(String.raw`^${QTY}(?:\s*(?:-|–|bis|to)\s*${QTY})?\s*(.*)$`, 'i');

/** One ingredient line from a recipe into amount, unit, name and note. */
export function parseIngredient(line: string): Ingredient {
  let text = line.replace(/\s+/g, ' ').trim();
  let note = '';
  // "Zwiebel, gewürfelt" / "Tomaten (aus der Dose)" – the part after the comma is a note; a comma
  // between digits is a decimal comma ("0,5 l Milch", Mike 19.10.)
  let at = -1;
  for (let i = 1; i < text.length && at < 0; i++) {
    if (text[i] === ',' && !(/\d/.test(text[i - 1]!) && /\d/.test(text[i + 1] ?? ''))) at = i;
  }
  if (at > 0 && text.slice(at + 1).trim()) {
    note = text.slice(at + 1).trim();
    text = text.slice(0, at).trim();
  }
  const paren = /^(.+?)\s*\(([^)]*)\)\s*$/.exec(text);
  if (paren) {
    text = paren[1]!;
    note = note ? `${paren[2]}, ${note}` : paren[2]!;
  }
  let qty: number | null = null;
  let qtyMax: number | null = null;
  let unit = '';
  const m = LEAD.exec(text);
  if (m) {
    qty = parseQty(m[1]!);
    qtyMax = m[2] ? parseQty(m[2]) : null;
    let rest = m[3]!.trim();
    // "400g Spaghetti" and "400 g Spaghetti", "2 EL. Öl"
    const u = /^([A-Za-zÄÖÜäöüß]+)\.?(?:\s+|$)(.*)$/.exec(rest);
    if (u && UNIT_SET.has(u[1]!.toLowerCase())) {
      unit = u[1]!;
      rest = u[2]!.trim();
    }
    text = rest;
  }
  return {
    qty,
    qtyMax: qtyMax && qty && qtyMax > qty ? qtyMax : null,
    unit,
    name: (text || line).slice(0, 80).trim(),
    note: note.slice(0, 80).trim(),
  };
}

/** Amount for other servings, rounded the way people cook (no "133,33 g"). */
export function scaleQty(qty: number, from: number, to: number): number {
  const v = (qty * to) / Math.max(1, from);
  if (v >= 100) return Math.round(v / 5) * 5;
  if (v >= 10) return Math.round(v);
  return Math.round(v * 4) / 4; // quarters: ¼ TL, 1½ Dosen
}

const NICE: Record<string, string> = { '0.25': '¼', '0.5': '½', '0.75': '¾' };

/** 1.5 → "1½", 0.25 → "¼", 400 → "400" (German decimal comma otherwise). */
export function formatQty(v: number): string {
  const whole = Math.floor(v);
  const rest = String(Math.round((v - whole) * 100) / 100);
  if (NICE[rest]) return `${whole || ''}${NICE[rest]}`;
  return String(Math.round(v * 100) / 100).replace('.', ',');
}

/** "400 g", "2–3 Zehen", "" – the amount column of an ingredient for `servings`. */
export function amountText(i: Ingredient, from: number, to: number): string {
  if (i.qty === null) return i.unit;
  const a = formatQty(scaleQty(i.qty, from, to));
  const b = i.qtyMax ? `–${formatQty(scaleQty(i.qtyMax, from, to))}` : '';
  return [`${a}${b}`, i.unit].filter(Boolean).join(' ');
}

/**
 * What goes on the shopping list for one ingredient: the article name and the amount as its
 * spec – sorted into the shop's aisle like anything typed in.
 */
export function toListEntry(
  i: Ingredient,
  from: number,
  to: number,
): { name: string; spec: string; aisle: Aisle } {
  return { name: i.name, spec: amountText(i, from, to), aisle: aisleOf(i.name) };
}

// ---------------------------------------------------------------------------------------------
// Steps: timers in the text, times in ISO 8601
// ---------------------------------------------------------------------------------------------

const TIMER =
  /(\d+)(?:\s*(?:-|–|bis|to)\s*\d+)?\s*(minuten|minute|min\.?|mins?|minutes?|stunden|stunde|std\.?|hours?|hrs?)\b/i;

/** "10 Minuten köcheln lassen" → 10, "1 Stunde backen" → 60; null without a time. */
export function timerOf(text: string): number | null {
  const m = TIMER.exec(text);
  if (!m) return null;
  const n = Number(m[1]);
  const hours = /^(stunde|std|hour|hr)/i.test(m[2]!);
  const v = hours ? n * 60 : n;
  return v > 0 && v <= 24 * 60 ? v : null;
}

/** "PT1H30M" → 90, "P0DT0H25M" → 25; null for anything else. */
export function isoMinutes(v: unknown): number | null {
  if (typeof v !== 'string') return null;
  const m = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:\d+S)?)?$/i.exec(v.trim());
  if (!m) return null;
  const total = Number(m[1] ?? 0) * 1440 + Number(m[2] ?? 0) * 60 + Number(m[3] ?? 0);
  return total > 0 ? total : null;
}

// ---------------------------------------------------------------------------------------------
// Filters (1.21): computed where we can, set by the family where we cannot
// ---------------------------------------------------------------------------------------------

export type RecipeFilter = 'quick' | 'veggie' | 'kids' | 'longAgo';

export function matchesFilter(
  r: Pick<Recipe, 'minutes' | 'tags' | 'lastCooked'>,
  f: RecipeFilter,
  now: number,
): boolean {
  switch (f) {
    case 'quick':
      return r.minutes !== null && r.minutes <= QUICK_MINUTES;
    case 'veggie':
    case 'kids':
      return r.tags.includes(f);
    case 'longAgo':
      return r.lastCooked === null || now - r.lastCooked > LONG_AGO_DAYS * 86_400_000;
  }
}

// Search (1.22): forgiving like a person – "Kaese" finds "Käse", "kaiserschmar" and "Lasange"
// find the dish (Mike, 17.10.: "konkret suchen über Freitext mit einer gewissen Fuzzyness").

/** Lower case, umlauts and accents folded the same way however they are typed. */
const fold = (s: string) =>
  s
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/ä|ae/g, 'a')
    .replace(/ö|oe/g, 'o')
    .replace(/ü|ue/g, 'u')
    .normalize('NFD')
    .replace(/\p{M}/gu, '');

const wordsOf = (s: string) =>
  fold(s)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

/** Edit distance, stopping early once it exceeds `max` (search words are short). */
function within(a: string, b: string, max: number): boolean {
  if (Math.abs(a.length - b.length) > max) return false;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      const v = Math.min(
        prev[j]! + 1,
        row[j - 1]! + 1,
        prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      row.push(v);
      best = Math.min(best, v);
    }
    if (best > max) return false;
    prev = row;
  }
  return prev[b.length]! <= max;
}

/** How well one typed word fits one word of the recipe: 3 inside it, 1 with a typo, 0 not. */
function wordScore(typed: string, word: string): number {
  if (word.includes(typed)) return 3;
  // short words get no typo: "Eis" with one letter changed would find half the book
  if (typed.length < 4) return 0;
  const max = typed.length >= 7 ? 2 : 1;
  // a typo in the beginning of a longer word ("tomatn" in "tomatensosse") or in the whole word
  return within(typed, word.slice(0, typed.length), max) || within(typed, word, max) ? 1 : 0;
}

/**
 * How well a recipe fits the search, 0 = not at all. Every typed word must fit somewhere; the
 * title counts double, so "Linsen" puts the Linsencurry above a soup with a few lentils.
 */
export function searchScore(
  r: { title: string; ingredients: { name: string }[] },
  q: string,
): number {
  const typed = wordsOf(q);
  if (typed.length === 0) return 1;
  const title = wordsOf(r.title);
  const rest = r.ingredients.flatMap((i) => wordsOf(i.name));
  let score = 0;
  for (const t of typed) {
    const inTitle = Math.max(0, ...title.map((w) => wordScore(t, w))) * 2;
    const inRest = Math.max(0, ...rest.map((w) => wordScore(t, w)));
    const best = Math.max(inTitle, inRest);
    if (best === 0) return 0;
    score += best;
  }
  return score;
}

/** Search in title and ingredients – umlauts however typed, small typos forgiven. */
export const matchesSearch = (r: { title: string; ingredients: { name: string }[] }, q: string) =>
  searchScore(r, q) > 0;

// Short words only as whole words: "gehackte Tomaten" are no mince, "Champignons" no ham,
// "Polenta" no duck (found by the Ideenbuch test, 17.10.).
const MEAT =
  /(fleisch|(?<!ge)hack|hähnchen|huhn|hühnchen|pute|rind|schwein|speck|schinken|salami|wurst|würstchen|lachs|thunfisch|fisch|garnele|shrimp|chorizo|bacon|chicken|beef|pork|\bham\b|sausage|salmon|tuna|fish|prawn|lamb|lamm|\bente|duck|anchovis|sardelle)/i;

/** Suggest "vegetarisch" when no ingredient is meat or fish – the family confirms it. */
export const looksVeggie = (ingredients: Pick<Ingredient, 'name'>[]) =>
  ingredients.length > 0 && !ingredients.some((i) => MEAT.test(i.name));

// ---------------------------------------------------------------------------------------------
// Link import (schema.org/Recipe as JSON-LD – what most recipe sites give Google)
// ---------------------------------------------------------------------------------------------

type Json = unknown;

const asArray = (v: Json): Json[] =>
  Array.isArray(v) ? v : v === undefined || v === null ? [] : [v];
const isType = (node: Json, t: string) =>
  !!node &&
  typeof node === 'object' &&
  asArray((node as Record<string, Json>)['@type']).some(
    (x) => typeof x === 'string' && x.toLowerCase() === t,
  );

/** Find the Recipe node in any JSON-LD shape: single, list, @graph, nested. */
export function findRecipeNode(data: Json, depth = 0): Record<string, Json> | null {
  if (depth > 6 || !data || typeof data !== 'object') return null;
  if (isType(data, 'recipe')) return data as Record<string, Json>;
  const values = Array.isArray(data) ? data : Object.values(data as Record<string, Json>);
  for (const v of values) {
    const hit = findRecipeNode(v, depth + 1);
    if (hit) return hit;
  }
  return null;
}

const ENTITIES: Record<string, string> = {
  amp: '&',
  quot: '"',
  apos: "'",
  lt: '<',
  gt: '>',
  nbsp: ' ',
};

/** Plain text: no tags, entities decoded, spaces tidied. */
export function plainText(v: unknown): string {
  if (typeof v !== 'string') return '';
  return v
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&(#\d+|#x[0-9a-f]+|[a-z]+);/gi, (m, e: string) => {
      if (e[0] === '#') {
        const code = e[1]?.toLowerCase() === 'x' ? parseInt(e.slice(2), 16) : Number(e.slice(1));
        return Number.isFinite(code) ? String.fromCodePoint(code) : m;
      }
      return ENTITIES[e.toLowerCase()] ?? m;
    })
    .replace(/[ \t]+/g, ' ')
    .replace(/ ([.,;:!?])/g, '$1')
    .replace(/\s*\n\s*/g, '\n')
    .trim();
}

/** Steps from recipeInstructions: text, HowToStep, HowToSection, or one block with lines. */
function stepsOf(v: Json, depth = 0): string[] {
  if (depth > 4) return [];
  if (typeof v === 'string') {
    // one block: split on line breaks, or on numbered sentences "1. … 2. …"
    const text = plainText(v);
    const lines = text
      .split(/\n+/)
      .map((l) => l.replace(/^\s*(?:\d+[.)]|schritt \d+:?|step \d+:?)\s*/i, '').trim());
    return lines.filter(Boolean);
  }
  if (Array.isArray(v)) return v.flatMap((x) => stepsOf(x, depth + 1));
  if (v && typeof v === 'object') {
    const o = v as Record<string, Json>;
    if (isType(o, 'howtosection')) return stepsOf(o['itemListElement'], depth + 1);
    const text = o['text'] ?? o['name'] ?? o['description'];
    return text ? stepsOf(text, depth + 1) : [];
  }
  return [];
}

/** Servings from recipeYield: 4, "4", "4 Portionen", ["4", "4 Stück"]. */
function servingsOf(v: Json): number {
  for (const x of asArray(v)) {
    const m = /(\d+)/.exec(String(x));
    if (m) {
      const n = Number(m[1]);
      if (n >= 1 && n <= 50) return n;
    }
  }
  return 4;
}

/**
 * A schema.org Recipe → our draft. Pure: the server fetches the page and finds the JSON-LD,
 * this turns it into ingredients, steps with timers and times. No photo – the picture belongs
 * to the site (Mike, 15.10.: decided, families add their own).
 */
export function recipeFromJsonLd(
  node: Record<string, Json>,
  url: string | null,
): RecipeDraft | null {
  const title = plainText(node['name']).slice(0, 120);
  if (!title) return null;
  const ingredients = asArray(node['recipeIngredient'] ?? node['ingredients'])
    .map((x) => plainText(x))
    .filter(Boolean)
    .slice(0, 80)
    .map(parseIngredient);
  const steps = stepsOf(node['recipeInstructions'])
    .slice(0, 60)
    .map((text) => ({ text: text.slice(0, 1000), minutes: timerOf(text) }));
  const total =
    isoMinutes(node['totalTime']) ??
    ((isoMinutes(node['prepTime']) ?? 0) + (isoMinutes(node['cookTime']) ?? 0) || null);
  let note = '';
  try {
    note = url ? new URL(url).hostname.replace(/^www\./, '') : '';
  } catch {
    note = '';
  }
  return RecipeDraft.parse({
    title,
    servings: servingsOf(node['recipeYield']),
    minutes: total && total <= 48 * 60 ? total : null,
    ingredients,
    steps,
    tags: looksVeggie(ingredients) ? ['veggie'] : [],
    kidsTip: '',
    source: { kind: 'link', url, note },
  });
}

/** All JSON-LD blocks of a page (the server hands in the HTML it fetched). */
export function jsonLdBlocks(html: string): Json[] {
  const out: Json[] = [];
  const re = /<script[^>]+type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
  for (let m = re.exec(html); m; m = re.exec(html)) {
    try {
      out.push(JSON.parse(m[1]!.trim()));
    } catch {
      // some sites put several objects or comments in one block – skip what does not parse
    }
  }
  return out;
}

/** The recipe on a page, or null when the page has no machine-readable recipe. */
export function recipeFromHtml(html: string, url: string | null): RecipeDraft | null {
  for (const block of jsonLdBlocks(html)) {
    const node = findRecipeNode(block);
    if (node) return recipeFromJsonLd(node, url);
  }
  return null;
}

/** Is a draft good enough to show without "please check"? Ingredients and steps both there. */
export const draftComplete = (d: RecipeDraft) => d.ingredients.length > 0 && d.steps.length > 0;

// ---------------------------------------------------------------------------------------------
// AI import (Plus): a screenshot, a photo of a cookbook page or a messy recipe page
// ---------------------------------------------------------------------------------------------

/** The answer we ask the AI for – lines as written, we parse amounts ourselves. */
export const RECIPE_RESPONSE_SCHEMA = {
  type: 'object',
  properties: {
    title: { type: 'string' },
    servings: { type: 'integer', description: 'Portionen; 4 wenn nicht angegeben' },
    minutes: { type: 'integer', description: 'Gesamtzeit in Minuten; 0 wenn unbekannt' },
    ingredients: {
      type: 'array',
      items: { type: 'string' },
      description: 'Eine Zutat pro Zeile, Menge vorne, wie im Rezept: "400 g Spaghetti"',
    },
    steps: {
      type: 'array',
      items: { type: 'string' },
      description: 'Ein Arbeitsschritt pro Eintrag',
    },
    kidsTip: { type: 'string', description: 'Ein kurzer Tipp für den Kinderteller oder leer' },
    unsure: {
      type: 'array',
      items: { type: 'string' },
      description: 'Zutaten- oder Schrittzeilen, die schlecht lesbar oder geraten sind',
    },
  },
  required: ['title', 'ingredients', 'steps'],
} as const;

/**
 * Instructions for the AI. The marker "[recipe]" lets the test fake answer with a recipe. We
 * never ask for a picture or anything about the family – only the recipe itself.
 */
export function recipeInstructions(locale: 'de' | 'en'): string {
  // Written fully in the family's language: a German instruction with "answer in English" (or
  // the other way round) made the model mix languages (Mike, 16.10.: German photo, German app,
  // English recipe).
  if (locale === 'en') {
    return [
      '[recipe] You read exactly one recipe from an image (screenshot, photo of a cookbook page) or a text.',
      'Answer in English only. If the recipe is in another language, translate title, ingredients, steps and kidsTip into English – nothing invented, nothing left out.',
      'Ingredients: one per line, amount and unit first ("400 g spaghetti", "1 tbsp oil"). Never convert amounts.',
      'Steps: one step per entry, without numbers. Keep times in the text ("simmer for 10 minutes").',
      'Servings and total time only if stated or clearly visible (otherwise 4 and 0).',
      'kidsTip: at most one short sentence, only if something suits children (e.g. add the spice later), otherwise empty.',
      'unsure: every line that is hard to read or guessed, exactly as in ingredients/steps.',
      'If the image or text holds no recipe, return an empty title.',
    ].join('\n');
  }
  return [
    '[recipe] Du liest genau ein Kochrezept aus einem Bild (Screenshot, Foto einer Kochbuchseite) oder einem Text.',
    'Antworte nur auf Deutsch. Ist das Rezept in einer anderen Sprache, übersetze Titel, Zutaten, Schritte und kidsTip ins Deutsche – nichts erfinden, nichts weglassen.',
    'Zutaten: eine pro Zeile, Menge und Einheit vorne ("400 g Spaghetti", "1 EL Öl"). Mengen nie umrechnen.',
    'Schritte: ein Arbeitsschritt pro Eintrag, ohne Nummern. Zeiten im Text lassen ("10 Minuten köcheln").',
    'Portionen und Gesamtzeit nur, wenn sie dastehen oder klar erkennbar sind (sonst 4 bzw. 0).',
    'kidsTip: höchstens ein kurzer Satz, nur wenn sich etwas für Kinder anbietet (z. B. Schärfe später dazugeben), sonst leer.',
    'unsure: jede Zeile, die schlecht lesbar oder geraten ist, genau so wie in ingredients/steps.',
    'Ist auf dem Bild oder im Text kein Rezept, gib einen leeren title zurück.',
  ].join('\n');
}

export interface AiRecipe {
  draft: RecipeDraft;
  /** Lines to check ("bitte prüfen") – matched against the parsed ingredients and steps. */
  unsure: string[];
}

/** The AI's answer → our draft (same parsing as typed or linked recipes). */
export function recipeFromAi(raw: unknown, source: RecipeSource): AiRecipe | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const title = plainText(o['title']).slice(0, 120);
  if (!title) return null;
  const lines = (v: unknown) =>
    asArray(v)
      .map((x) => plainText(x))
      .filter(Boolean);
  const ingredients = lines(o['ingredients']).slice(0, 80).map(parseIngredient);
  const steps = lines(o['steps'])
    .slice(0, 60)
    .map((text) => ({ text: text.slice(0, 1000), minutes: timerOf(text) }));
  const servings = Number(o['servings']);
  const minutes = Number(o['minutes']);
  const draft = RecipeDraft.parse({
    title,
    servings: Number.isInteger(servings) && servings >= 1 && servings <= 50 ? servings : 4,
    minutes: Number.isInteger(minutes) && minutes > 0 && minutes <= 48 * 60 ? minutes : null,
    ingredients,
    steps,
    tags: looksVeggie(ingredients) ? ['veggie'] : [],
    kidsTip: plainText(o['kidsTip']).slice(0, 200),
    source,
  });
  return { draft, unsure: lines(o['unsure']).slice(0, 40) };
}

/** Page text for "Mit KI aufräumen": the readable part of a page, short enough for one request. */
export function pageText(html: string, max = 20_000): string {
  return plainText(
    html
      .replace(/<(script|style|noscript|svg|header|footer|nav)[\s\S]*?<\/\1>/gi, ' ')
      .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n'),
  ).slice(0, max);
}

/**
 * The ingredients a step mentions ("Zwiebel glasig dünsten" → 1 Zwiebel) – shown with the step
 * in the cooking view. Simple word stems, so "Zwiebeln"/"Zwiebel" and "Tomaten"/"Tomate" meet.
 */
export function ingredientsInStep<T extends Pick<Ingredient, 'name'>>(
  text: string,
  ingredients: T[],
): T[] {
  const words = text
    .toLowerCase()
    .split(/[^a-zäöüß]+/)
    .filter((w) => w.length >= 3);
  const stem = (w: string) => w.replace(/(en|n|e|s)$/, '');
  const stems = new Set(words.map(stem));
  return ingredients.filter((i) =>
    i.name
      .toLowerCase()
      .split(/[^a-zäöüß]+/)
      .filter((w) => w.length >= 3)
      .some((w) => stems.has(stem(w)) || words.some((x) => x.endsWith(w))),
  );
}

// ---------------------------------------------------------------------------------------------
// The demo family's recipes (1.21) – written by us, so the collection is not empty when trying
// ---------------------------------------------------------------------------------------------

interface DemoRecipe {
  title: string;
  servings: number;
  minutes: number;
  ingredients: string[];
  steps: string[];
  tags: RecipeTag[];
  kidsTip: string;
}

const DEMO: Record<'de' | 'en', DemoRecipe[]> = {
  de: [
    {
      title: 'Cremige Tomatenpasta',
      servings: 4,
      minutes: 25,
      ingredients: [
        '400 g Spaghetti',
        '1 Zwiebel, gewürfelt',
        '1 Knoblauchzehe',
        '1 Dose gehackte Tomaten',
        '200 g Sahne',
        '1 TL Salz',
        'Basilikum',
      ],
      steps: [
        'Spaghetti in Salzwasser nach Packung kochen.',
        'Zwiebel und Knoblauch in etwas Öl glasig dünsten.',
        'Tomaten dazugeben und 10 Minuten köcheln lassen.',
        'Sahne einrühren, mit Salz abschmecken und mit den Spaghetti mischen.',
        'Mit Basilikum servieren.',
      ],
      tags: ['veggie', 'kids'],
      kidsTip: 'Basilikum extra – nicht jeder mag Grünes auf den Nudeln.',
    },
    {
      title: 'Ofengemüse mit Feta',
      servings: 4,
      minutes: 40,
      ingredients: [
        '800 g Kartoffeln',
        '2 Paprika',
        '1 Zucchini',
        '1 rote Zwiebel',
        '200 g Feta',
        '3 EL Olivenöl',
        '1 TL Paprikapulver',
      ],
      steps: [
        'Ofen auf 200 °C vorheizen.',
        'Kartoffeln vierteln, Gemüse in grobe Stücke schneiden.',
        'Alles mit Öl und Paprikapulver mischen und auf einem Blech verteilen.',
        '30 Minuten backen.',
        'Feta darüberbröseln und weitere 5 Minuten backen.',
      ],
      tags: ['veggie'],
      kidsTip: '',
    },
    {
      title: 'Hähnchen-Wraps',
      servings: 4,
      minutes: 20,
      ingredients: [
        '8 Wraps',
        '400 g Hähnchenbrust',
        '1 Gurke',
        '2 Tomaten',
        '150 g Joghurt',
        '1 Salat',
      ],
      steps: [
        'Hähnchen in Streifen schneiden und 8 Minuten in der Pfanne braten.',
        'Gurke, Tomaten und Salat klein schneiden.',
        'Wraps kurz erwärmen, mit Joghurt bestreichen und füllen.',
      ],
      tags: ['kids'],
      kidsTip: 'Jeder füllt seinen Wrap selbst.',
    },
  ],
  en: [
    {
      title: 'Creamy tomato pasta',
      servings: 4,
      minutes: 25,
      ingredients: [
        '400 g spaghetti',
        '1 onion, diced',
        '1 clove garlic',
        '1 can chopped tomatoes',
        '200 g cream',
        '1 tsp salt',
        'Basil',
      ],
      steps: [
        'Cook the spaghetti in salted water as the packet says.',
        'Soften the onion and garlic in a little oil.',
        'Add the tomatoes and simmer for 10 minutes.',
        'Stir in the cream, season with salt and mix with the spaghetti.',
        'Serve with basil.',
      ],
      tags: ['veggie', 'kids'],
      kidsTip: 'Basil on the side – not everyone likes green bits on their pasta.',
    },
    {
      title: 'Roast vegetables with feta',
      servings: 4,
      minutes: 40,
      ingredients: [
        '800 g potatoes',
        '2 peppers',
        '1 courgette',
        '1 red onion',
        '200 g feta',
        '3 tbsp olive oil',
        '1 tsp paprika',
      ],
      steps: [
        'Heat the oven to 200 °C.',
        'Quarter the potatoes and cut the vegetables into chunks.',
        'Mix everything with the oil and paprika and spread it on a tray.',
        'Bake for 30 minutes.',
        'Crumble the feta over it and bake for another 5 minutes.',
      ],
      tags: ['veggie'],
      kidsTip: '',
    },
    {
      title: 'Chicken wraps',
      servings: 4,
      minutes: 20,
      ingredients: [
        '8 wraps',
        '400 g chicken breast',
        '1 cucumber',
        '2 tomatoes',
        '150 g yoghurt',
        '1 lettuce',
      ],
      steps: [
        'Cut the chicken into strips and fry for 8 minutes.',
        'Chop the cucumber, tomatoes and lettuce.',
        'Warm the wraps briefly, spread with yoghurt and fill.',
      ],
      tags: ['kids'],
      kidsTip: 'Everyone fills their own wrap.',
    },
  ],
};

/** The demo family's recipes as drafts, in the family's language. */
export const demoRecipes = (locale: 'de' | 'en'): RecipeDraft[] =>
  DEMO[locale].map((r) =>
    RecipeDraft.parse({
      title: r.title,
      servings: r.servings,
      minutes: r.minutes,
      ingredients: r.ingredients.map(parseIngredient),
      steps: r.steps.map((text) => ({ text, minutes: timerOf(text) })),
      tags: r.tags,
      kidsTip: r.kidsTip,
      source: { kind: 'own', url: null, note: '' },
    }),
  );
